import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button, Card, CpfRevelavel, DataTable, EmptyState, FilterBar, FilterCheckboxGroup, FilterChips, OlhoBotao,
  Pagination, type Column,
} from "@atlas/ui/web";
import { aplicarFiltros, contagens, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../../lib/recorte";
import { matchAnyKeys } from "../../../lib/text-search";
import { situacaoEncerrada } from "../../../lib/contrato-status";
import { useFiltros } from "../../../lib/use-filtros";
import { atlas } from "../../../lib/sdk";
import { downloadCsv } from "../../../lib/csv";
import { totalEmFolha, valorSolicitado } from "@atlas/types";
import type { BancoContratoFull } from "@atlas/sdk";

const ALL_FIELDS = [
  { value: "adf", label: "ADF" },
  { value: "cpf", label: "CPF" },
  { value: "matricula", label: "Matrícula" },
  { value: "nome", label: "Servidor" },
  { value: "tipoContrato", label: "Tipo" },
  { value: "origem", label: "Origem" },
  { value: "totalParcelas", label: "Parcelas" },
  { value: "valorParcela", label: "Valor parcela" },
  { value: "valorFinanciado", label: "Valor solicitado" },
  { value: "totalEmFolha", label: "Total em folha" },
  { value: "taxaAm", label: "Taxa" },
  { value: "cetAm", label: "CET" },
  { value: "saldoDevedor", label: "Saldo devedor" },
  { value: "situacao", label: "Situação" },
];

/** Colunas de dinheiro: formatadas em reais na tabela. */
const CAMPOS_EM_REAIS = new Set(["valorParcela", "valorFinanciado", "totalEmFolha", "saldoDevedor"]);

/**
 * A linha como o gerador a exibe e exporta.
 *
 * O contrato LEGADO sem informe nao tem principal, taxa nem CET: o backend grava
 * zero neles, e zero ali significa "nao sei" (feature 012). Vira `null` aqui —
 * a tabela mostra "—" e o CSV sai com a celula vazia, em vez de afirmar um
 * emprestimo de R$ 0,00 a 0% ao mes.
 */
function linhaDoGerador(l: BancoContratoFull): Record<string, unknown> {
  const conhecido = l.dadosFinanceirosConhecidos !== false;
  return {
    ...l,
    origem: l.origem === "legado" ? "Legado" : "Atlas",
    // Completo no CSV: relatorio vai sem censura, no escopo do banco (14/10/2026).
    // Na tabela, mascarado atras do olho.
    cpf: l.cpf || l.cpfMasked,
    valorFinanciado: valorSolicitado({
      valorParcela: l.valorParcela,
      totalParcelas: l.totalParcelas,
      valorFinanciado: l.valorFinanciado,
      dadosFinanceirosConhecidos: l.dadosFinanceirosConhecidos,
    }),
    totalEmFolha: l.cronogramaPendente ? null : totalEmFolha(l),
    saldoDevedor: l.cronogramaPendente ? null : l.saldoDevedor,
    taxaAm: conhecido ? l.taxaAm : null,
    cetAm: conhecido ? l.cetAm : null,
  };
}

const DEFAULT_SELECTED = new Set(["adf", "nome", "valorFinanciado", "situacao"]);

// A regra de "saiu da carteira efetiva" saiu daqui em 04/09/2026 para
// `lib/contrato-status`: Consignacoes usa a MESMA regra nos indicadores de
// carteira ativa, e duas copias divergiriam na primeira situacao nova que o
// backend emitisse. (Cliente 28/07/2026 pediu o recorte no Gerador tambem, pra
// o CSV exportado nao arrastar cancelado.)
const situacaoInativa = situacaoEncerrada;

/**
 * Chave da preferencia de colunas.
 *
 * As colunas escolhidas viviam so' em `useState`: sumiam ao navegar e voltar, e
 * o reset de FILTROS as apagava. Escolher campos e' o proposito declarado desta
 * tela ("Escolha os campos que quer exibir e exportar"), entao a escolha
 * precisa durar mais que a visita.
 *
 * `localStorage` e' o lugar certo aqui: e' preferencia de apresentacao de UM
 * operador, nao dado do negocio. Nao vale um endpoint.
 */
const CHAVE_COLUNAS = "atlas:banco:gerador:colunas";

function lerColunasSalvas(): Set<string> {
  try {
    const cru = localStorage.getItem(CHAVE_COLUNAS);
    if (!cru) return new Set(DEFAULT_SELECTED);
    const arr = JSON.parse(cru) as unknown;
    if (!Array.isArray(arr)) return new Set(DEFAULT_SELECTED);
    // Filtra contra ALL_FIELDS: uma coluna renomeada ou removida no codigo nao
    // pode ressuscitar de uma preferencia velha.
    const validas = arr.filter((v): v is string => typeof v === "string" && ALL_FIELDS.some((f) => f.value === v));
    return validas.length > 0 ? new Set(validas) : new Set(DEFAULT_SELECTED);
  } catch {
    // Aba privada, storage bloqueado, JSON corrompido: cai no default em vez de
    // derrubar a tela.
    return new Set(DEFAULT_SELECTED);
  }
}

/** Situacao como escolha POSITIVA — ver o comentario no controle. */
type EscopoSituacao = "" | "ativas" | "encerradas";

export function BancoRelatorioGerador() {
  const [selected, setSelected] = useState<Set<string>>(lerColunasSalvas);
  // Um olho para a coluna de CPF inteira (diretriz de 14/10/2026).
  const [cpfsRevelados, setCpfsRevelados] = useState(false);
  const [search, setSearch] = useState("");
  const [escopo, setEscopo] = useState<EscopoSituacao>("");
  const all = useQuery({ queryKey: ["banco", "rel", "consig", "all"], queryFn: () => atlas.banco.relatorioConsignacoes() });

  // Persiste a escolha de colunas. Falha de storage nao pode quebrar a tela.
  useEffect(() => {
    try { localStorage.setItem(CHAVE_COLUNAS, JSON.stringify([...selected])); } catch { /* sem persistencia, segue */ }
  }, [selected]);

  const cols: Column<Record<string, unknown>>[] = useMemo(
    () =>
      ALL_FIELDS.filter((f) => selected.has(f.value)).map((f) => ({
        key: f.value,
        header: f.label,
        render: (r) => {
          const v = r[f.value];
          if (f.value === "cpf") {
            return <CpfRevelavel cpf={String(v ?? "")} cpfMasked={String(r.cpfMasked ?? "")} revelado={cpfsRevelados} />;
          }
          if (typeof v === "number" && CAMPOS_EM_REAIS.has(f.value)) return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
          if (typeof v === "number" && (f.value === "taxaAm" || f.value === "cetAm")) return `${(v * 100).toFixed(2)}%`;
          return v == null ? "—" : String(v);
        },
      })),
    [selected, cpfsRevelados],
  );

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  // FR-005: mexer no recorte volta pra primeira pagina.
  //
  // Esta tela era a UNICA das tres de relatorio do banco sem isto. O sintoma
  // nao parece bug de paginacao: o operador esta na pagina 4, digita na busca,
  // e a tabela fica vazia — porque o recorte encolheu para menos de 4 paginas.
  // `recortar` limita a fatia, mas o `pagina` do estado continua velho, entao a
  // barra ainda anuncia a posicao antiga. Le-se como "minha busca nao achou
  // nada".
  useEffect(() => { setPagina(1); }, [search, escopo]);
  const allRows = useMemo(() => (all.data?.linhas ?? []).map(linhaDoGerador), [all.data?.linhas]);

  // BUSCA RESTRITA A IDENTIDADE. Antes o `hay` incluia `tipoContrato` e
  // `situacao`, e isso duplicava dimensoes que tem (ou deveriam ter) filtro
  // proprio: digitar "cancelado" trazia linhas pela coluna Situacao, e o
  // operador nunca sabia se achou pelo nome ou pelo estado.
  const termo = search.trim();
  const filtros = useMemo(() => ({
    escopo: escopo
      ? (r: Record<string, unknown>) => (escopo === "encerradas" ? situacaoInativa(r.situacao) : !situacaoInativa(r.situacao))
      : null,
    busca: termo
      ? (r: Record<string, unknown>) => matchAnyKeys(r, termo, ["adf", "matricula", "nome"])
      : null,
  }), [escopo, termo]);

  const rows = useMemo(() => aplicarFiltros(allRows, filtros), [allRows, filtros]);
  const semEscopo = useMemo(() => aplicarFiltros(allRows, filtros, "escopo"), [allRows, filtros]);
  const contEscopo = useMemo(
    () => contagens(semEscopo, (r) => (situacaoInativa(r.situacao) ? "encerradas" : "ativas")),
    [semEscopo],
  );

  /**
   * O RESET DE FILTROS NAO MEXE NAS COLUNAS, e essa era a correcao principal
   * deste grupo.
   *
   * `resetarFiltros` fazia `setSelected(new Set(DEFAULT_SELECTED))`: o operador
   * montava o relatorio dele, clicava em limpar filtros para trocar o recorte, e
   * perdia a montagem. Escolher campos e' preferencia de APRESENTACAO, nao
   * recorte de dado — sao coisas com ciclos de vida diferentes, e agora cada uma
   * tem o seu botao.
   */
  const filtrosUi = useFiltros(
    [
      { nome: "busca", ligado: search !== "", limpar: () => setSearch("") },
      { nome: "situacao", ligado: escopo !== "", limpar: () => setEscopo("") },
    ],
    setPagina,
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Relatórios
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Gerador de Relatórios</h1>
          <p style={{ color: "var(--text-muted)", marginTop: 4 }}>Escolha os campos que quer exibir e exportar.</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {selected.has("cpf") ? (
            <OlhoBotao rotulo="CPFs" revelado={cpfsRevelados} onAlternar={() => setCpfsRevelados((v) => !v)} />
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const out = rows.map((r) => {
                const o: Record<string, unknown> = {};
                for (const f of ALL_FIELDS) if (selected.has(f.value)) o[f.value] = r[f.value];
                return o;
              });
              downloadCsv("relatorio-personalizado.csv", out);
            }}
          >
            ⬇ Exportar CSV
          </Button>
        </div>
      </header>

      {/* CAMPOS DO RELATORIO — bloco proprio, fora da barra de filtros.
          Estava dentro do `FilterBar` e por isso parecia filtro: mesmo quadro,
          mesma linha, e o mesmo botao de limpar levava as duas coisas. Sao
          ciclos de vida diferentes — a escolha de colunas dura entre visitas
          (localStorage) e tem reset proprio; o recorte de dado e' da sessao. */}
      <Card>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <div style={{ display: "grid", gap: 2 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
              Campos do relatório
            </span>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Valem para a tabela e para o CSV. A escolha fica salva neste navegador.
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelected(new Set(DEFAULT_SELECTED))}
            disabled={[...selected].sort().join() === [...DEFAULT_SELECTED].sort().join()}
          >
            ↻ Campos padrão
          </Button>
        </div>
        <FilterCheckboxGroup options={ALL_FIELDS} selected={selected} onChange={setSelected} />
      </Card>

      <FilterBar
        layout="linha"
        searchValue={search}
        onSearchChange={filtrosUi.filtrar(setSearch)}
        searchPlaceholder="Buscar por ADF, matrícula ou nome"
        {...filtrosUi.propsFilterBar}
        actions={
          /* SELECAO POSITIVA no lugar do "Excluir cancelados / recusados /
             expirados". A negacao composta era o controle mais dificil de
             conferir da tela: para saber o que estava vendo, o operador tinha
             de lembrar quais tres estados o checkbox tirava — e "expirado" nao
             e' o mesmo que "cancelado" para quem cobra. Positivo, com
             contagem, o recorte se le sem decorar a regra. */
          <FilterChips
            variant="segmentado"
            label="Situação"
            value={escopo}
            onChange={(v) => filtrosUi.filtrar(setEscopo)(v as EscopoSituacao)}
            options={[
              { value: "", label: "Todas", count: semEscopo.length },
              { value: "ativas", label: "Ativas", count: contEscopo.ativas ?? 0 },
              { value: "encerradas", label: "Encerradas", count: contEscopo.encerradas ?? 0 },
            ]}
          />
        }
      />

      {selected.size === 0 ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)", border: "1px dashed var(--border-strong)", borderRadius: 12 }}>
          Selecione pelo menos uma coluna para exibir o relatório.
        </div>
      ) : (
        <>
        <DataTable
          columns={cols}
          rows={recortar(rows, pagina, porPagina).itens}
          rowKey={(r) => String(r.adf)}
          loading={all.isLoading}
          emptyState={
            all.isError
              ? "Não foi possível carregar os dados do relatório."
              : <EmptyState
                  motivo={motivoVazio(allRows.length, rows.length)}
                  filtroVazio="Nenhuma linha bate com esse recorte."
                  semDados="Nenhuma consignação na carteira ainda."
                  onLimpar={filtrosUi.limparFiltros}
                />
          }
        />
        <Pagination
          total={rows.length}
          pagina={pagina}
          porPagina={porPagina}
          onPagina={setPagina}
          onPorPagina={setPorPagina}
          rotulo="linhas"
        />
        </>
      )}
    </div>
  );
}
